import React, { useCallback, useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import LinearProgress from "@material-ui/core/LinearProgress";
import Tooltip from "@material-ui/core/Tooltip";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import StatusChip from "../../components/StatusChip";
import TicketHeatmap from "../../components/TicketHeatmap";
import { useThemeContext } from "../../context/DarkMode";
import { semantic } from "../../theme/tokens";

const useStyles = makeStyles((theme) => ({
  tile: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  tileLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  tileValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    lineHeight: 1.15,
    fontVariantNumeric: "tabular-nums",
  },
  tileHint: { color: theme.palette.text.secondary },
  panel: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  panelTitle: { marginBottom: theme.spacing(0.5) },
  panelSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    display: "block",
  },
  section: { marginBottom: theme.spacing(3) },
  numeric: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  barRow: { marginBottom: theme.spacing(1.5) },
  barHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: 4,
  },
  barTrack: {
    height: 8,
    borderRadius: 4,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,0.14)"
        : theme.palette.grey[100],
  },
  barFill: { height: 8, borderRadius: 4 },
  muted: { color: theme.palette.text.hint },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4, 0),
    textAlign: "center",
  },
  slaBar: { height: 6, borderRadius: 3, marginTop: 6 },
}));

const pad = (n) => String(n).padStart(2, "0");
const isoDate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Duração legível — "3m 20s" comunica melhor que "200 s". */
const fmtDuration = (seconds) => {
  if (seconds == null) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const StatTile = ({ label, value, hint, tone }) => {
  const classes = useStyles();
  const color = tone ? semantic[tone]?.main : undefined;
  return (
    <Paper variant="outlined" className={classes.tile}>
      <span className={classes.tileLabel}>{label}</span>
      <span className={classes.tileValue} style={color ? { color } : undefined}>
        {value}
      </span>
      {hint && (
        <Typography variant="caption" className={classes.tileHint}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
};

const OperationsAnalytics = () => {
  const classes = useStyles();
  const { darkMode } = useThemeContext();

  const [start, setStart] = useState(
    isoDate(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000))
  );
  const [end, setEnd] = useState(isoDate(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get("/analytics/operations", {
        params: { start, end },
      });
      setData(result);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sla = data?.sla;
  const lost = data?.lostReasons;
  const agents = data?.agents || [];

  const slaTone =
    sla?.firstResponse?.rate == null
      ? undefined
      : sla.firstResponse.rate >= 90
      ? "success"
      : sla.firstResponse.rate >= 70
      ? "warning"
      : "danger";

  const totalOpen = agents.reduce((sum, a) => sum + a.openTickets, 0);
  const totalPending = agents.reduce((sum, a) => sum + a.pendingTickets, 0);
  const maxLost = Math.max(1, ...(lost?.reasons || []).map((r) => r.total));

  return (
    <MainContainer>
      <PageHeader
        title="Operação"
        subtitle="Carga por agente, tempo de resposta, cumprimento de SLA, horários de pico e motivos de perda."
        filters={
          <>
            <TextField
              type="date"
              label="De"
              InputLabelProps={{ shrink: true }}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
            <TextField
              type="date"
              label="Até"
              InputLabelProps={{ shrink: true }}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
            {sla?.policy && (
              <Typography variant="caption" className={classes.muted}>
                Meta de 1ª resposta: {sla.policy.firstResponseMinutes} min
              </Typography>
            )}
          </>
        }
      />

      {loading && <LinearProgress />}

      <div style={{ paddingTop: 16 }}>
        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Tickets em aberto"
              value={totalOpen + totalPending}
              hint={`${totalOpen} em atendimento · ${totalPending} aguardando`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="SLA cumprido"
              value={
                sla?.firstResponse?.rate != null
                  ? `${sla.firstResponse.rate}%`
                  : "—"
              }
              tone={slaTone}
              hint={
                sla?.firstResponse?.total
                  ? `${sla.firstResponse.met} de ${sla.firstResponse.total} respondidos no prazo`
                  : "Sem tickets respondidos no período"
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Tempo médio de resposta"
              value={fmtDuration(sla?.firstResponse?.avgSeconds)}
              hint="Da abertura até a primeira resposta do agente"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatTile
              label="Estourando agora"
              value={sla?.pendingBreached ?? "—"}
              tone={sla?.pendingBreached ? "danger" : undefined}
              hint="Abertos, sem resposta e já fora do prazo"
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" className={`${classes.panel} ${classes.section}`}>
          <Typography variant="h4" className={classes.panelTitle}>
            Por agente
          </Typography>
          <Typography variant="caption" className={classes.panelSubtitle}>
            Carga é o estado atual; resposta, SLA e fechamentos são do período
            selecionado.
          </Typography>

          {agents.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Agente</TableCell>
                  <TableCell className={classes.numeric}>Em atendimento</TableCell>
                  <TableCell className={classes.numeric}>Aguardando</TableCell>
                  <TableCell className={classes.numeric}>Fechados</TableCell>
                  <TableCell className={classes.numeric}>Resposta média</TableCell>
                  <TableCell className={classes.numeric}>Mediana</TableCell>
                  <TableCell>SLA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow key={agent.userId ?? "unassigned"}>
                    <TableCell>
                      <Typography variant="subtitle2">{agent.name}</Typography>
                    </TableCell>
                    <TableCell className={classes.numeric}>
                      {agent.openTickets}
                    </TableCell>
                    <TableCell className={classes.numeric}>
                      {agent.pendingTickets}
                    </TableCell>
                    <TableCell className={classes.numeric}>
                      {agent.closedInPeriod}
                    </TableCell>
                    <TableCell className={classes.numeric}>
                      {fmtDuration(agent.avgFirstResponseSeconds)}
                    </TableCell>
                    <TableCell className={classes.numeric}>
                      {fmtDuration(agent.medianFirstResponseSeconds)}
                    </TableCell>
                    <TableCell>
                      {agent.slaRate == null ? (
                        <span className={classes.muted}>—</span>
                      ) : (
                        <Tooltip
                          title={`${agent.slaMet} no prazo · ${agent.slaBreached} fora`}
                        >
                          <span>
                            <StatusChip
                              dot
                              tone={
                                agent.slaRate >= 90
                                  ? "success"
                                  : agent.slaRate >= 70
                                  ? "warning"
                                  : "danger"
                              }
                              label={`${agent.slaRate}%`}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className={classes.empty}>
              Nenhum agente com movimentação no período.
            </div>
          )}
        </Paper>

        <Grid container spacing={2} className={classes.section}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" className={classes.panel}>
              <Typography variant="h4" className={classes.panelTitle}>
                Quando os tickets chegam
              </Typography>
              <Typography variant="caption" className={classes.panelSubtitle}>
                Volume de abertura por dia da semana e hora — use para dimensionar
                escala de plantão.
              </Typography>
              {data?.heatmap ? (
                <TicketHeatmap
                  cells={data.heatmap.cells}
                  max={data.heatmap.max}
                  total={data.heatmap.total}
                  busiest={data.heatmap.busiest}
                  isDark={darkMode}
                />
              ) : (
                <div className={classes.empty}>Sem dados no período.</div>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper variant="outlined" className={classes.panel}>
              <Typography variant="h4" className={classes.panelTitle}>
                Motivos de perda
              </Typography>
              <Typography variant="caption" className={classes.panelSubtitle}>
                {lost?.totalLost
                  ? `${lost.totalLost} negócios perdidos · ${fmtMoney(
                      lost.totalLostValue
                    )} deixados na mesa${
                      lost.lossRate != null
                        ? ` · taxa de perda ${lost.lossRate}%`
                        : ""
                    }`
                  : "Nenhum negócio perdido no período."}
              </Typography>

              {(lost?.reasons || []).map((reason) => (
                <div key={reason.lostReasonId ?? "none"} className={classes.barRow}>
                  <div className={classes.barHeader}>
                    <Typography variant="body2">{reason.name}</Typography>
                    <Typography variant="body2" className={classes.numeric}>
                      {reason.total} · {fmtMoney(reason.value)}
                    </Typography>
                  </div>
                  <div className={classes.barTrack}>
                    <div
                      className={classes.barFill}
                      style={{
                        width: `${(reason.total / maxLost) * 100}%`,
                        backgroundColor: darkMode ? "#5598e7" : "#256abf",
                      }}
                    />
                  </div>
                </div>
              ))}

              {!lost?.reasons?.length && !loading && (
                <div className={classes.empty}>
                  Nada perdido no período — ou os negócios perdidos não tiveram
                  motivo informado.
                </div>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>
    </MainContainer>
  );
};

export default OperationsAnalytics;
